import * as THREE from "three";

/**
 * Uma capa de disco de 12" pendurada na cena, feita em código.
 *
 * Serve para pôr um lançamento novo no quarto sem reabrir o Blender e
 * reexportar os 34 MB do .glb — a capa vem de um .jpg em static/ e o resto é
 * um plano.
 */

/* 12 polegadas em metros. A cena usa metro como unidade: a câmera fica a
   1.8 e o playerHeight do fpControls é 1.7. */
const SLEEVE_SIZE = 0.3048;

export class RecordSleeve {
  /**
   * @param {object} options
   * @param {string} options.cover – caminho da imagem da capa
   * @param {number} [options.size] – lado da capa, em metros
   */
  constructor({ cover, size = SLEEVE_SIZE }) {
    this.size = size;

    const texture = new THREE.TextureLoader().load(cover);
    texture.colorSpace = THREE.SRGBColorSpace;

    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(size, size),
      new THREE.MeshStandardMaterial({
        map: texture,
        roughness: 0.62,
        metalness: 0,
        /* Encostada em algo, só a frente aparece — mas dupla face evita que
           ela suma se acabar virada para o outro lado. */
        side: THREE.DoubleSide,
      })
    );
    this.mesh.name = "record_sleeve";

    /*
     * Ordem YXZ para o giro ser previsível depois de deitar a capa.
     *
     * Na ordem padrão (XYZ) o Y é aplicado antes do X, então, com a capa
     * deitada, mexer no "giro" a fazia cambalear em vez de rodar no lugar.
     * Com YXZ o Y fica por fora e vira rotação em torno da vertical do
     * mundo, que é o que se espera de um disco largado numa superfície.
     */
    this.mesh.rotation.order = "YXZ";
  }

  /** Deita a capa, como os discos que já estão largados pelo quarto. */
  lieFlat() {
    this.mesh.rotation.x = -Math.PI / 2;
    return this;
  }

  /**
   * Copia o tamanho de um disco que já está no quarto.
   *
   * Mede a caixa da geometria em espaço local e aplica só a escala do mundo,
   * de propósito. A caixa envolvente no mundo é alinhada aos eixos, e os
   * discos estão largados tortos no sofá (o advent_record está a 123°), então
   * ela mede o retângulo que cerca o disco girado, não o disco: dava 0.673 m
   * para uma face que tem 0.487 m, e a capa saía 38% maior que os vizinhos.
   *
   * @param {THREE.Object3D} root
   * @param {string} meshName – o disco a imitar
   */
  matchSize(root, meshName) {
    const target = root.getObjectByName(meshName);

    if (!target) {
      console.warn(
        `[record] disco "${meshName}" nao existe no .glb; a capa fica no tamanho atual`
      );
      return false;
    }

    // O node pode ser um Group: procura a primeira geometria de verdade.
    let mesh = null;
    target.traverse((object) => {
      if (!mesh && object.isMesh) mesh = object;
    });

    if (!mesh) {
      console.warn(`[record] "${meshName}" nao tem geometria; tamanho mantido`);
      return false;
    }

    root.updateMatrixWorld(true);
    mesh.geometry.computeBoundingBox();

    const local = mesh.geometry.boundingBox.getSize(new THREE.Vector3());
    const scale = new THREE.Vector3();
    mesh.getWorldScale(scale);

    /* Maior dimensão horizontal: a face do disco. A vertical é a espessura
       da capa, que aqui não interessa. */
    this.setWorldSize(Math.max(local.x * scale.x, local.z * scale.z));
    return true;
  }

  /**
   * Recorta uma borda da textura, em fração de cada lado.
   *
   * A foto da capa no Bandcamp tem margem em volta da capa de verdade, e
   * como a capa do PERX é branca sobre fundo branco essa margem não se vê —
   * só faz a arte parecer menor que a dos discos vizinhos, cuja textura
   * ocupa a face inteira. Recortar aqui é mais barato do que reeditar o
   * arquivo, e não mexe no que está no disco.
   *
   * @param {number} amount – 0 usa a imagem inteira; 0.05 tira 5% de cada lado
   */
  setTrim(amount) {
    const map = this.mesh.material.map;
    if (!map) return;

    map.repeat.set(1 - amount * 2, 1 - amount * 2);
    map.offset.set(amount, amount);
    map.needsUpdate = true;
  }

  /**
   * Pendura a capa dentro do quarto.
   *
   * Entra como filha do .glb de propósito: assim ela some junto com o quarto
   * quando o jogador entra na galeria, sem precisar de mais um caso no
   * WorldManager. O preço é herdar a escala 0.3 do modelo, que este método
   * desfaz — senão a capa sairia com 9 cm em vez de 30.
   *
   * @param {THREE.Object3D} parent – a cena do glTF já carregada
   */
  attachTo(parent) {
    parent.add(this.mesh);
    parent.updateMatrixWorld(true);
    this.setWorldSize(this.size);

    return this;
  }

  /**
   * Lado da capa em metros de mundo, independente da escala do pai.
   *
   * Absoluto e não relativo ao tamanho anterior de propósito: o lil-gui
   * grava o valor novo no objeto antes de chamar o onChange, então um cálculo
   * que olhasse para o "tamanho antigo" já leria o novo e nunca sairia do
   * lugar.
   */
  setWorldSize(meters) {
    const parentScale = new THREE.Vector3();
    this.mesh.parent.getWorldScale(parentScale);
    this.mesh.scale.setScalar(meters / (this.size * parentScale.x));
  }

  /** Lado da capa em metros de mundo, já contando a escala do quarto. */
  get worldSize() {
    const parentScale = new THREE.Vector3();
    this.mesh.parent.getWorldScale(parentScale);
    return this.size * this.mesh.scale.x * parentScale.x;
  }

  /**
   * Posiciona a capa a partir de um mesh do modelo.
   *
   * A referência é o centro da caixa que envolve o mesh, e não a posição
   * dele: a geometria do .glb está assada nos vértices, então `position` é
   * (0,0,0) em praticamente tudo lá dentro e mirar nela põe a capa no canto
   * da sala.
   *
   * @param {THREE.Object3D} root   – a cena do glTF
   * @param {string} anchorName     – nome do mesh de referência
   * @param {THREE.Vector3} offset  – deslocamento em metros, no mundo
   */
  placeFrom(root, anchorName, offset = new THREE.Vector3()) {
    const anchor = root.getObjectByName(anchorName);

    if (!anchor) {
      console.warn(
        `[record] ancora "${anchorName}" nao existe no .glb; ` +
          "a capa fica na origem do quarto"
      );
      return false;
    }

    root.updateMatrixWorld(true);

    const center = new THREE.Box3()
      .setFromObject(anchor)
      .getCenter(new THREE.Vector3())
      .add(offset);

    // O mesh é filho do modelo escalado, então o alvo entra em espaço local.
    this.mesh.position.copy(this.mesh.parent.worldToLocal(center));
    return true;
  }

  /* Posição em metros no mundo, para os controles do lil-gui não terem que
     pensar na escala 0.3 do quarto. */
  get worldPosition() {
    return this.mesh.parent.localToWorld(this.mesh.position.clone());
  }

  setWorldPosition(x, y, z) {
    this.mesh.position.copy(
      this.mesh.parent.worldToLocal(new THREE.Vector3(x, y, z))
    );
  }
}
